import { motion } from "framer-motion";
import { Check, Lock, ShieldCheck, FileDown, ShoppingCart } from "lucide-react";
import { toast } from "sonner";
import { CHECKOUT_URL } from "@/lib/site";

const INCLUDED = [
  "170+ page main guide (PDF, instant download)",
  "Full Recipe Collection — 40+ teas & infusions",
  "7-Day Reset, 14-Day Plan & 30-Day Routine",
  "Printable checklists, tools & references",
  "Lifetime access — read on phone, tablet or computer",
];

const PAYMENTS = ["VISA", "Mastercard", "Amex", "PayPal", "Apple Pay"];

export default function Pricing() {
  const handleBuy = () => {
    if (CHECKOUT_URL) {
      window.open(CHECKOUT_URL, "_blank", "noopener");
    } else {
      toast.info("Checkout link coming soon — connect your payment link (Hotmart, Kiwify, Stripe…) to activate this button.");
    }
  };

  return (
    <section id="pricing" className="relative py-16 sm:py-24 lg:py-32 bg-[#1C3A27] overflow-hidden" data-testid="pricing-section">
      <div className="absolute -top-40 left-1/2 -translate-x-1/2 w-[720px] h-[720px] rounded-full bg-[#D4A359]/[0.07] blur-3xl pointer-events-none" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <motion.div
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="grid lg:grid-cols-[1.15fr_1fr] gap-12 lg:gap-16 items-center"
        >
          <div>
            <p className="text-xs sm:text-sm font-bold tracking-[0.24em] uppercase text-[#D4A359] mb-4">
              Start your tea journey today
            </p>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-[#FAF7F2] leading-tight" data-testid="pricing-heading">
              Small steps. Real habits. A better you.
            </h2>

            <div className="mt-8 flex items-end gap-4" data-testid="pricing-price">
              <span className="font-display text-6xl sm:text-7xl font-bold text-[#FAF7F2] leading-none">$22</span>
              <div className="pb-1.5">
                <p className="text-sm text-[#FAF7F2]/60 line-through">$49</p>
                <p className="text-xs font-bold tracking-[0.18em] uppercase text-[#D4A359]">One-time payment</p>
              </div>
              <span className="mb-2 ml-2 rounded-full bg-[#D4A359] text-[#12261A] text-[11px] font-extrabold px-3 py-1 uppercase tracking-wide">
                55% off today
              </span>
            </div>

            <ul className="mt-8 space-y-3.5" data-testid="pricing-included-list">
              {INCLUDED.map((item, i) => (
                <motion.li
                  key={item}
                  initial={{ opacity: 0, x: -18 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.15 + i * 0.08, duration: 0.5 }}
                  className="flex items-start gap-3 text-sm sm:text-base text-[#FAF7F2]/90"
                >
                  <span className="w-5 h-5 rounded-full border border-[#D4A359]/60 flex items-center justify-center shrink-0 mt-0.5">
                    <Check className="w-3 h-3 text-[#D4A359]" strokeWidth={3.5} />
                  </span>
                  {item}
                </motion.li>
              ))}
            </ul>
          </div>

          <div className="relative">
            <motion.div
              initial={{ opacity: 0, y: 32 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2, duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
              className="rounded-2xl bg-[#12261A] border border-[#D4A359]/30 p-8 sm:p-10 shadow-[0_40px_80px_-30px_rgba(0,0,0,0.6)]"
            >
              {/* guarantee seal */}
              <div className="absolute -top-10 -right-4 sm:-right-8 w-24 h-24 sm:w-28 sm:h-28" data-testid="guarantee-seal">
                <svg viewBox="0 0 100 100" className="w-full h-full animate-[spin_28s_linear_infinite]">
                  <defs>
                    <path id="circlePath" d="M 50,50 m -36,0 a 36,36 0 1,1 72,0 a 36,36 0 1,1 -72,0" />
                  </defs>
                  <circle cx="50" cy="50" r="48" fill="#D4A359" />
                  <circle cx="50" cy="50" r="38" fill="none" stroke="#12261A" strokeWidth="1" strokeDasharray="3 3" />
                  <text fill="#12261A" fontSize="9.2" fontWeight="800" letterSpacing="1.6">
                    <textPath href="#circlePath">30-DAY MONEY-BACK GUARANTEE • </textPath>
                  </text>
                </svg>
                <ShieldCheck className="absolute inset-0 m-auto w-8 h-8 text-[#12261A]" />
              </div>

              <p className="text-xs font-bold tracking-[0.24em] uppercase text-[#D4A359] mb-2">Digital edition</p>
              <h3 className="font-display text-2xl sm:text-3xl font-bold text-[#FAF7F2] leading-snug">
                The Sustainable Tea &amp; Weight Loss Method
              </h3>
              <p className="mt-3 text-sm text-[#FAF7F2]/65 leading-relaxed">
                Try it for 30 days. If it's not for you, request a refund from your Hotmart account and you'll get every cent back — no questions asked.
              </p>

              <motion.button
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
                onClick={handleBuy}
                className="cta-shine relative overflow-hidden mt-8 w-full rounded-full bg-[#D4A359] text-[#12261A] font-extrabold text-sm sm:text-base px-8 py-4 tracking-wide hover:bg-[#C29247] transition-colors duration-300 flex items-center justify-center gap-2.5 shadow-[0_18px_40px_-12px_rgba(212,163,89,0.45)]"
                data-testid="pricing-buy-button"
              >
                <ShoppingCart className="w-5 h-5" />
                GET MY DIGITAL GUIDE NOW
              </motion.button>

              <div className="mt-4 flex items-center justify-center gap-2 text-[11px] text-[#FAF7F2]/55">
                <FileDown className="w-3.5 h-3.5" />
                Instant download · Secure checkout
              </div>

              <div className="mt-6 flex flex-wrap justify-center gap-2" data-testid="payment-badges">
                {PAYMENTS.map((p) => (
                  <span
                    key={p}
                    className="rounded-md bg-[#FAF7F2]/10 border border-[#FAF7F2]/15 text-[#FAF7F2]/80 text-[10px] font-extrabold tracking-wider px-2.5 py-1"
                  >
                    {p}
                  </span>
                ))}
                <span className="rounded-md bg-[#FAF7F2]/10 border border-[#FAF7F2]/15 text-[#FAF7F2]/80 text-[10px] font-extrabold tracking-wider px-2.5 py-1 flex items-center gap-1">
                  <Lock className="w-3 h-3" /> SSL 256-BIT
                </span>
              </div>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
